// Single source of truth for how the Forecast Manager splits forecasts into its
// WATCHLIST and RECENT sections.
//
// Why this module exists: the split used to be inlined in each of the two log
// variants the Forecast Manager had before the legacy one was retired, and the
// two had already drifted — the tracking-off copy tested only `pinned[f.id]`
// and omitted `isResolvedWatchlisted`, so a resolved-but-watchlisted forecast
// stayed pinned in one mode and slid into Recent in the other. One predicate,
// used by both, makes that class of drift impossible.
//
// The second (worse) bug this fixes: both copies derived the watchlist by
// FILTERING the capped display list. A tracked forecast older than the display
// cap was therefore absent from the list the filter ran over, so it could not
// appear in WATCHLIST at all — it silently vanished from the UI while the server
// still reported it as tracked and kept reforecasting it on schedule. Raising the
// cap only moves that cliff; the structural fix is to source watchlist rows from
// the FULL store (see selectWatchlistForecasts in selectors.ts) and merge them in
// here via mergeWatchlistRows, so watchlist membership never depends on the cap.

import { isResolvedWatchlisted } from "../tracking.js";

/**
 * Listable in the Forecast Manager? Only questions the user ASKED.
 *
 * Machine-minted trigger questions (`triggerOrigin` set) are excluded — they
 * surface on the parent's timeline (the TRIGGER CONTRACT block) and live in the
 * data plane, so listing them alongside user-authored forecasts crowded the
 * manager out (up to 10 minted per completed forecast). Introduced in #2598;
 * kept here rather than inlined per-component so the two log variants can't
 * disagree about what is listable, the same way they had already drifted on what
 * "watchlisted" means.
 *
 * Shape-agnostic like makeIsWatchlisted: `triggerOrigin` is present on both a
 * mapped LegacyForecast and a raw store Run (hydrate stamps it), so the cheap
 * full-store scan can filter on it without mapping.
 *
 * `triggerOrigin` alone is NOT a sufficient test: lineage can be absent on a minted row
 * (`trigger_origin` and `trigger_source_forecast_id` both null), and such a row then
 * reads as user-authored. It also cannot render — a minted row is never forecast — so it
 * lists as a card that opens to an empty timeline.
 *
 * The fallback is the id, which the server owns: `mintClientId` builds
 * `trig-<parentForecastId>-<suffix>` and `client_id` round-trips as the wire `id`. TWO
 * suffixes are live — a 64-hex content hash, and the legacy positional `e<N>` that older
 * rows keep — so both are matched. Matching the full SHAPE rather than the `trig-`
 * prefix is what keeps an unrelated id that merely starts with `trig-` listable.
 *
 * Checked in ADDITION to the origin, never instead of it: a minted row that kept its
 * lineage is caught by the cheaper test, and a mint that changes its id shape still
 * filters on the origin.
 *
 * @param {unknown} f
 * @returns {boolean}
 */
const MINTED_TRIGGER_ID = /^trig-.+-(?:[0-9a-f]{64}|e\d+)$/;

export const isMintedTriggerId = (id) => typeof id === "string" && MINTED_TRIGGER_ID.test(id);

export const isUserAsked = (f) => !!f && !f.triggerOrigin && !isMintedTriggerId(f.id ?? f.runId);

/**
 * Build the watchlist predicate for a given server-derived `pinned` map.
 *
 * Deliberately shape-agnostic: it reads `id ?? runId` so it works on BOTH a
 * mapped LegacyForecast (`id`) and a raw store Run (`runId`) — the two carry the
 * same identifier, and `isResolvedWatchlisted` only reads `contractStatus` +
 * `schedule`, which are present under those names on both. That lets the cheap
 * full-store scan in selectWatchlistForecasts reuse this exact predicate rather
 * than reimplementing it against the raw shape (which is how the original drift
 * started).
 *
 * @param {Record<string, unknown>|null|undefined} pinned server `pinned` map
 * @returns {(f: unknown) => boolean}
 */
export const makeIsWatchlisted = (pinned) => (f) =>
  !!f && (!!(pinned && pinned[f.id ?? f.runId]) || isResolvedWatchlisted(f));

/**
 * Merge full-store watchlist rows into the capped display list.
 *
 * `display` is the newest-N display list; `watchlistRows` are watchlisted
 * forecasts sourced from the full store, which may or may not already be in
 * `display`. Dedupes by id, keeping the `display` instance when present so any
 * overlay applied to it (e.g. the time-travel snapshot on the active row) wins.
 * Appends the out-of-window extras at the end — ordering is applied downstream
 * by orderForecasts, and the section split puts them in WATCHLIST regardless of
 * position.
 *
 * REFERENCE STABILITY: returns the `display` array ITSELF whenever there is
 * nothing to append, and only allocates when rows are actually added.
 * `selectLegacyForecasts` caches its output array specifically so an unchanged
 * list keeps its identity across store updates (and `orderForecasts` has the same
 * `return forecasts` fast path); copying unconditionally here — e.g. an eager
 * `.filter(Boolean)` — would defeat both and retrigger every downstream
 * useMemo/useEffect keyed on the list. Falsy entries are therefore left in place
 * rather than filtered out: `selectLegacyForecasts` can legitimately yield nulls,
 * and every consumer (splitForecastLists via isUserAsked, and the `f &&` guards
 * elsewhere) already tolerates them.
 *
 * @param {Array<unknown>} display capped display list (newest first)
 * @param {Array<unknown>} watchlistRows watchlisted rows from the full store
 * @returns {Array<unknown>} display ∪ watchlistRows, display instances preferred
 */
export function mergeWatchlistRows(display, watchlistRows) {
  const base = display || [];
  const extra = watchlistRows || [];
  if (!extra.length) return base;
  const seen = new Set();
  for (const f of base) if (f) seen.add(f.id);
  const missing = extra.filter((f) => f && !seen.has(f.id));
  return missing.length ? base.concat(missing) : base;
}

/**
 * Merge the owner's forecasts and the ones shared with them into ONE list,
 * ordered by when the viewer RECEIVED each — newest first.
 *
 * There is no separate "Shared with me" section any more (collaborative-forecasts
 * design): a forecast shared with you is one of your forecasts, and it belongs in
 * the same list, at the point in time you got it. `receivedAt` is what makes the
 * two lanes comparable — the owner leg reports its creation time and the shared
 * leg reports the grant time (see `rowToWire`), so one sort key covers both.
 *
 * Falls back to `createdAt` for a row from a server that predates `receivedAt`,
 * then to 0, so an old cached payload sorts to the bottom instead of throwing.
 *
 * Deduped by `id`, OWNED instances preferred: the same forecast can arrive on
 * both legs (you own it AND it is shared into a team you are in), and the owned
 * copy is the one carrying the fields the shared leg strips (`schedule`,
 * `notify`, `contractStatus`). Preferring the shared instance would silently
 * blank those on your own row.
 *
 * @param {Array<any>} owned rows from `GET /api/forecasts`
 * @param {Array<any>} shared rows from `GET /api/forecasts/shared`
 * @returns {Array<any>} one list, newest-received first
 */
export function mergeByReceivedAt(owned, shared) {
  const base = owned || [];
  const extra = (shared || []).filter(Boolean);
  // Identity fast path for the common case (most users have nothing shared with
  // them): return the store's own array rather than a fresh copy, so a consumer
  // memoizing on it does not re-render every time. Same convention as
  // `mergeWatchlistRows` above, which likewise tolerates nulls in `base` —
  // every consumer guards with `f &&` or `isUserAsked`.
  if (!extra.length) return base;
  const kept = base.filter(Boolean);
  const seen = new Set(kept.map((f) => f.id));
  const merged = kept.concat(extra.filter((f) => !seen.has(f.id)));
  const at = (f) => {
    const t = Date.parse(f.receivedAt ?? f.createdAt ?? "");
    return Number.isNaN(t) ? 0 : t;
  };
  // `merged` is already a fresh array (concat), so sorting it in place cannot
  // reorder the store's own array under a caller rendering it elsewhere.
  return merged.sort((a, b) => at(b) - at(a));
}

/**
 * Split an ordered forecast list into the two Forecast Manager sections.
 *
 * Non-listable rows (see isUserAsked) are dropped from BOTH sections.
 *
 * NEITHER section is windowed. `FORECAST_DISPLAY_CAP` upstream is the single
 * bound on how much reaches here — deliberately one number rather than two
 * stacked ones, since a second, independently-chosen bound layered on the first
 * is precisely the shape that produced the watchlist-eviction bug (#2606).
 *
 * History, so the next reader doesn't reintroduce it: RECENT carried a
 * `.slice(0, 64)` from #2534 until it was removed here. It never truncated
 * anything — the upstream display cap was 50 for its entire lifetime, so slicing
 * a ≤50-row list to 64 was a no-op. It first became load-bearing only when #2606
 * raised that cap to 1000, and was deleted rather than tuned because no rationale
 * for the value was ever recorded and no measurement supported it. WATCHLIST was
 * never windowed: it is bounded by the server tracking rules plus
 * WATCHLIST_LIMIT, and silently dropping a row the user explicitly tracks is the
 * exact failure this module exists to prevent.
 *
 * @param {Array<unknown>} forecasts ordered list (watchlist rows already merged in)
 * @param {(f: unknown) => boolean} isWatchlisted from makeIsWatchlisted
 * @returns {{watchlist: Array<unknown>, recent: Array<unknown>}}
 */
export function splitForecastLists(forecasts, isWatchlisted) {
  const watchlist = [];
  const recent = [];
  for (const f of forecasts || []) {
    if (!isUserAsked(f)) continue;
    if (isWatchlisted(f)) watchlist.push(f);
    else recent.push(f);
  }
  return { watchlist, recent };
}
